"use client";

import { useFormikContext } from "formik";
import { useCallback } from "react";

import isValidPercentage from "@shared/functions/isValidPercentage";

import Input from "@client/components/formik/input";

const MAX_DECIMAL_PLACES = 2;
const DEFAULT_MAX_VALUE = 99.99;
const DECIMAL_REGEX = /[^\d.]/g;

type PercentageChangeEvent = React.ChangeEvent<HTMLInputElement> & {
  target: { value: string };
};

interface Props {
  name: string;
  maxValue?: number; // Allow configurable max value
  decimalPrecision?: number;
}

export default function FormikPercentage({
  name,
  maxValue = DEFAULT_MAX_VALUE,
  decimalPrecision = MAX_DECIMAL_PLACES,
}: Props) {
  const { setFieldValue, getFieldProps } = useFormikContext();
  const { value: fieldPropValue } = getFieldProps(name);

  const handleOnChange = useCallback(
    (e: PercentageChangeEvent) => {
      const inputValue = e.target.value.replace(DECIMAL_REGEX, ""); // Only allow numbers and decimal point

      // Handle empty input
      if (inputValue === "") {
        setFieldValue(name, "");
        return;
      }

      // Handle single decimal point
      if (inputValue === ".") {
        setFieldValue(name, "0.");
        return;
      }

      // Prevent multiple decimal points
      const parts = inputValue.split(".");
      if (parts.length > 2) return;

      // Handle integer part (remove leading zeros or keep single zero)
      let integerPart = parts[0];
      if (integerPart === "") {
        integerPart = "0";
      } else {
        integerPart = integerPart.replace(/^0+/, "") || "0"; // Remove leading zeros, default to '0' if all zeros
      }

      // Format decimal part
      const decimalPart = parts[1]?.slice(0, decimalPrecision) || "";

      // Construct the value
      let finalValue = integerPart;
      if (decimalPart || inputValue.endsWith(".")) {
        finalValue += "." + decimalPart;
      }

      // Early return if value is invalid
      if (!isValidPercentage(finalValue, maxValue)) return;

      setFieldValue(name, finalValue);
    },
    [name, setFieldValue, fieldPropValue],
  );

  const handleOnBlur = useCallback(() => {
    // Clean up trailing decimal point and zero decimals
    if (fieldPropValue) {
      const parts = fieldPropValue.split(".");
      const integerPart = parts[0];
      const decimalPart = parts[1];

      // Remove decimal point if it's trailing or if decimal part is zero/empty
      if (!decimalPart || parseFloat("0." + decimalPart) === 0) {
        setFieldValue(name, integerPart);
      }
    }
  }, [name, fieldPropValue, setFieldValue]);

  return (
    <div className="flex-col gap-2 relative">
      <Input
        name={name}
        inputMode="decimal"
        className="flex-1"
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        autoComplete="off"
      />

      <Input
        name={`hidden-${name}`}
        value={`${fieldPropValue}%`}
        /**
         * Using `inert` to hide the input from screen readers
         * inert={"true"} prevents the input from being focused
         * as well as being read by screen readers (aria-hidden
         **/
        inert
        className="absolute inset-0 pointer-events-none bg-transparent"
      />
    </div>
  );
}
